import React, { useMemo, useState } from 'react';

import { CustomOperatorValue } from '@aline/common';
import { link } from '@aline/routes';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import type {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridSortModel,
  GridToolbarProps,
} from '@mui/x-data-grid-premium';
import { DataGridPremium, GridLogicOperator, useGridApiContext } from '@mui/x-data-grid-premium';
import { File, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AlineCheckbox,
  allOwnerFilters,
  ConfirmationDialog,
  dateValueGetter,
  DropdownSelectButton,
  FiltersToolbarButton,
  getIsOwnerCellValue,
  getStringOperatorsWithSafeIsAnyOf,
  ListCountFooter,
  ListEmpty,
  NewReportMenu,
  OwnerCell,
  ReportFiltersPanel,
  ReportsListDateCell,
  ReportsListNameCell,
  SearchInput,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { ReportsEmptyIcon } from 'src/components/icons/ReportsEmptyIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { Apollo } from 'src/gql/generated';
import type { CurrentUserType } from 'src/gql/hooks/user';
import { useSelectCurrentUser } from 'src/gql/hooks/user';
import {
  createDataGridPremiumPaperStyles,
  FiltersAnchorProvider,
  useDataGridSearch,
  useDeleteReports,
  useFiltersAnchorState,
  useGetSelectedRowIds,
} from 'src/hooks';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

export type ReportsView = 'recently-created' | 'created-by-me';

const RECENTLY_CREATED_SORT_MODEL: GridSortModel = [{ field: 'createdAt', sort: 'desc' }];

interface ViewOption {
  icon: JSX.Element;
  label: string;
  value: ReportsView;
}

const VIEW_OPTIONS: ViewOption[] = [
  { icon: <File size={16} />, label: 'Recently created', value: 'recently-created' },
  { icon: <UserRound size={16} />, label: 'Created by me', value: 'created-by-me' },
];

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  menuIcon: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    minWidth: '0 !important',
    paddingRight: theme.spacing(1),
  },
  title: {
    flexShrink: 0,
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  toolbar: {
    flexShrink: 0,
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      '&:hover #overflow-menu': {
        visibility: 'visible',
      },
    },
    flexGrow: 1,
    minHeight: 0,
  },
}));

export type ReportsListRow = Pick<Apollo.Report, 'createdAt' | 'id' | 'name'> & {
  author: UserSubset;
};

export function ReportsList(): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const [activeReportsView, setActiveReportsView] = useState<ReportsView>('recently-created');
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({
    ids: new Set([]),
    type: 'include',
  });
  const { data, loading: isLoadingReports } = Apollo.useGetReportsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const columnDefs: GridColDef<ReportsListRow>[] = useMemo(() => {
    return [
      {
        field: 'name',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        flex: 1,
        headerName: 'Name',
        renderCell: ReportsListNameCell,
        type: 'string',
      },
      {
        field: 'author',
        filterOperators: allOwnerFilters,
        flex: 1,
        getApplyQuickFilterFn: (value: string) => {
          return cellValue => {
            return (
              getIsOwnerCellValue(cellValue) &&
              typeof cellValue.displayName === 'string' &&
              cellValue.displayName.toLowerCase().includes(value.toLowerCase())
            );
          };
        },
        headerName: 'Created by',
        renderCell: (params: GridRenderCellParams<ReportsListRow>) => (
          <OwnerCell showName row={{ owner: params.row.author }} />
        ),
        sortComparator: (v1, v2) => {
          const v1Name = getIsOwnerCellValue(v1) ? (v1.displayName ?? '') : '';
          const v2Name = getIsOwnerCellValue(v2) ? (v2.displayName ?? '') : '';
          return v1Name.localeCompare(v2Name);
        },
        type: 'string',
      },
      {
        field: 'createdAt',
        flex: 1,
        headerName: 'Date Created',
        renderCell: ReportsListDateCell,
        type: 'date',
        valueGetter: dateValueGetter,
      },
    ];
  }, []);
  const rows: ReportsListRow[] = useMemo(() => {
    return (
      data?.reports.map(({ author, createdAt, id, name }) => ({
        author,
        createdAt,
        id,
        name,
      })) ?? []
    );
  }, [data?.reports]);

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <div className={classes.container}>
        <div className={classes.listContainer}>
          <DataGridPremium
            checkboxSelection
            disableColumnMenu
            disableColumnResize
            showToolbar
            className={classes.xGrid}
            columns={columnDefs}
            initialState={{ sorting: { sortModel: RECENTLY_CREATED_SORT_MODEL } }}
            loading={isLoadingReports && !data}
            rowSelectionModel={selectedRowIds}
            rows={rows}
            slotProps={{
              filterPanel: {
                filterableColumns: 'visible',
              },
              footer: {
                noun: 'reports',
                totalCount: rows.length,
              },
              loadingOverlay: {
                noRowsVariant: 'linear-progress',
                variant: 'linear-progress',
              },
              panel: {
                placement: 'bottom-start',
                target: filtersAnchorEl ?? undefined,
              },
              toolbar: {
                activeReportsView,
                onReportsViewChange: setActiveReportsView,
              },
            }}
            slots={{
              baseCheckbox: AlineCheckbox,
              baseTooltip: GridAlineTooltip,
              filterPanel: ReportFiltersPanel,
              footer: ListCountFooter,
              noRowsOverlay: ReportsListEmpty,
              toolbar: ReportsListToolbar,
            }}
            sx={createDataGridPremiumPaperStyles}
            onRowClick={({ id }) => {
              navigate(link.ReportDetail({ reportId: String(id) }));
            }}
            onRowSelectionModelChange={setSelectedRowIds}
          />
        </div>
      </div>
    </FiltersAnchorProvider>
  );
}

function ReportsListEmpty(): JSX.Element {
  return (
    <ListEmpty
      icon={<ReportsEmptyIcon />}
      subtitle="Create reports to gain insights into your contract data."
      title="No Reports"
    />
  );
}

interface ReportsListToolbarProps extends GridToolbarProps {
  activeReportsView?: ReportsView;
  onReportsViewChange?: (view: ReportsView) => void;
}

function ReportsListToolbar({
  activeReportsView = 'recently-created',
  onReportsViewChange,
}: ReportsListToolbarProps): JSX.Element {
  const { classes: gridStyles, cx } = useStyles();
  const { classes } = useListToolbarStyles();
  const { deleteReports } = useDeleteReports();
  const { search, setSearch } = useDataGridSearch();
  const applyView = useApplyReportsView();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { selectedRowIds: selectedReportIds } = useGetSelectedRowIds();
  const hasSelectedReports = Boolean(selectedReportIds.length);

  const handleReportsViewChange = (view: ReportsView) => {
    onReportsViewChange?.(view);
    applyView(view);
  };

  return (
    <>
      <div className={cx(classes.toolbar, gridStyles.toolbar)}>
        <Typography className={gridStyles.title} variant="h6">
          Reports
        </Typography>
        <FiltersToolbarButton />
        <ReportsViewsDropdown
          activeView={activeReportsView}
          onViewChange={handleReportsViewChange}
        />
        <Spacer />
        <IconButton
          size="small"
          style={{ visibility: hasSelectedReports ? 'visible' : 'hidden' }}
          onClick={() => setDeleteDialogOpen(true)}
        >
          <TrashcanIcon />
        </IconButton>
        <SearchInput placeholder="Search reports..." search={search} setSearch={setSearch} />
        <NewReportMenu />
      </div>
      <ConfirmationDialog
        message="Are you sure you want to delete the selected reports?  This action cannot be undone."
        open={deleteDialogOpen}
        title="Delete reports?"
        onClickPrimary={async () => {
          await deleteReports(selectedReportIds);
          setDeleteDialogOpen(false);
        }}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </>
  );
}

interface ReportsViewsDropdownProps {
  activeView: ReportsView;
  onViewChange: (view: ReportsView) => void;
}

function ReportsViewsDropdown({
  activeView,
  onViewChange,
}: ReportsViewsDropdownProps): JSX.Element {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const activeOption = VIEW_OPTIONS.find(opt => opt.value === activeView) ?? VIEW_OPTIONS[0];

  return (
    <>
      <DropdownSelectButton
        ref={setAnchorEl}
        icon={activeOption.icon}
        label={activeOption.label}
        selected={menuOpen}
        variant="outlined"
        onClick={() => setMenuOpen(!menuOpen)}
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setMenuOpen(false)}
      >
        {VIEW_OPTIONS.map(opt => (
          <MenuItem
            key={opt.value}
            selected={opt.value === activeView}
            onClick={() => {
              setMenuOpen(false);
              onViewChange(opt.value);
            }}
          >
            <ListItemIcon className={classes.menuIcon}>{opt.icon}</ListItemIcon>
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

interface ReportsViewConfig {
  filterModel: GridFilterModel;
  // Omitted for views that should leave the current sort untouched (e.g. "created by me").
  sortModel?: GridSortModel;
}

type ReportsViewUser = Pick<NonNullable<CurrentUserType>, 'avatarColor' | 'displayName' | 'id'>;

/**
 * Maps a view to the grid filter/sort it applies. Returns `null` when the view
 * can't be applied yet — e.g. "created by me" before the current user loads.
 */
export function getReportsViewConfig(
  view: ReportsView,
  currentUser: ReportsViewUser | null
): ReportsViewConfig | null {
  if (view === 'recently-created') {
    return { filterModel: { items: [] }, sortModel: RECENTLY_CREATED_SORT_MODEL };
  }

  if (!currentUser) return null;

  return {
    filterModel: {
      items: [
        {
          field: 'author',
          id: uuid4(),
          operator: CustomOperatorValue.Owner,
          value: [
            {
              __typename: 'User',
              avatarColor: currentUser.avatarColor,
              displayName: currentUser.displayName,
              id: currentUser.id,
            },
          ],
        },
      ],
      logicOperator: GridLogicOperator.And,
    },
  };
}

function useApplyReportsView() {
  const apiRef = useGridApiContext();
  const currentUser = useSelectCurrentUser();

  return React.useCallback(
    (view: ReportsView) => {
      const config = getReportsViewConfig(view, currentUser);
      if (!config) return;

      apiRef.current.setFilterModel(config.filterModel);
      if (config.sortModel) apiRef.current.setSortModel(config.sortModel);
    },
    [apiRef, currentUser]
  );
}
